/**
 * Shape3D custom board -> cart attribute sync
 *
 * Duplicates per-line Shape3D board specs into order-level cart attributes so
 * they reach Shopify's "Additional details" panel and, from there, the Cin7 Core
 * sale Comments field via the connector's "Import additional properties" setting.
 *
 * Line item properties are left untouched. This only ever writes keys prefixed
 * with _S3D_, so the _blend_* attribution attributes are never disturbed
 * (/cart/update.js merges — it does not replace the attribute set).
 *
 * Install: assets/shape3d-attr-sync.js, loaded from theme.liquid with
 *   <script src="{{ 'shape3d-attr-sync.js' | asset_url }}" defer></script>
 * placed directly above </body> (outside any enclosing <script> block).
 *
 * Debugging: sessionStorage.setItem('S3D_DEBUG','1') then reload — the flag
 * survives the reload. Call S3D_SYNC() to force a sync by hand.
 */
(function () {
  'use strict';

  var PREFIX = '_S3D_';

  // Property key that identifies a line as a Shape3D custom board.
  var MARKER = 'Model';

  function debugOn() {
    if (window.S3D_DEBUG) return true;
    try { return sessionStorage.getItem('S3D_DEBUG') === '1'; } catch (e) { return false; }
  }

  function log() {
    if (!debugOn()) return;
    console.log.apply(console, ['[S3D]'].concat([].slice.call(arguments)));
  }

  /**
   * Extract the Shape3D board ID from the line.
   *
   * Shape3D leaves the SKU null on these variants. The board ID lives at the end
   * of the variant title instead: "1269.00 / 818376". Cin7 derives its product
   * code from the same value (custom-surfboard-1269.00-818376), so this remains
   * the join key between the attribute block and the Cin7 sale line.
   */
  function boardId(item) {
    var title = item.variant_title || item.title || '';
    var match = title.match(/(\d{4,})\s*$/);
    if (match) return match[1];

    // Some variants may carry it on the SKU instead.
    var sku = item.sku || '';
    match = sku.match(/-(\d{4,})$/);
    if (match) return match[1];

    // Last resort: Shape3D order number. Unique per board and stable, but does
    // not match the Cin7 product code, so the step 2 join becomes manual.
    var order = (item.properties || {})['_Order'];
    return order ? 'ORD' + String(order).replace(/\W/g, '') : null;
  }

  /** Flatten a board's properties into one compact, readable line. */
  function summarise(p) {
    return [
      p['Model'],
      p['_FinSystem'],
      p['_Order'] ? 'S3D#' + p['_Order'] : null,
      p['_Client'] ? 'Client ' + p['_Client'] : null,
      p['Print Sheet']
    ].filter(Boolean).join(' | ');
  }

  /** fetch() does not reject on 4xx/5xx, so check status explicitly. */
  function asJson(r) {
    if (!r.ok) throw new Error('HTTP ' + r.status + ' from ' + r.url);
    return r.json();
  }

  function getCart() {
    return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
      .then(asJson);
  }

  function syncOnce() {
    return getCart().then(function (cart) {
      var desired = {};

      (cart.items || []).forEach(function (item) {
        var props = item.properties || {};
        if (!props[MARKER]) return;            // not a custom board line

        var id = boardId(item);
        if (!id) return;                       // cannot namespace safely; skip

        var key = PREFIX + id;

        // Defensive: if two lines somehow resolve to the same ID, suffix rather
        // than silently overwrite one board's spec with another's.
        if (desired[key]) {
          var n = 2;
          while (desired[key + '_' + n]) n++;
          key = key + '_' + n;
        }

        desired[key] = summarise(props);
      });

      var attrs = cart.attributes || {};
      var updates = {};

      // Add or refresh
      Object.keys(desired).forEach(function (k) {
        if (attrs[k] !== desired[k]) updates[k] = desired[k];
      });

      // Clear stale keys for boards no longer in the cart.
      // Empty string is how /cart/update.js deletes an attribute.
      Object.keys(attrs).forEach(function (k) {
        if (k.indexOf(PREFIX) === 0 && !(k in desired)) updates[k] = '';
      });

      log('items', (cart.items || []).length,
          '| existing', Object.keys(attrs).filter(function (k) { return k.indexOf(PREFIX) === 0; }),
          '| desired', Object.keys(desired),
          '| updates', updates);

      if (!Object.keys(updates).length) return null;

      return fetch('/cart/update.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ attributes: updates })
      }).then(asJson).then(function (c) {
        log('wrote ok. now:', Object.keys(c.attributes || {}).filter(function (k) {
          return k.indexOf(PREFIX) === 0;
        }));
        return c;
      });
    });
  }

  /**
   * Serialise runs. Concurrent syncs race: two reads of a pre-change cart can
   * both write, and the later write restores a key the earlier one deleted.
   * A trailing re-run guarantees the final state reflects the final cart.
   */
  var inFlight = null;
  var queued = false;

  function run() {
    if (inFlight) { queued = true; return inFlight; }

    inFlight = syncOnce()
      .catch(function (e) {
        // Never block checkout on a sync failure, but do surface it.
        console.warn('[S3D] attribute sync failed:', e.message || e);
      })
      .then(function () {
        inFlight = null;
        if (queued) { queued = false; return run(); }
      });

    return inFlight;
  }

  // Run on load. Cheap (one /cart.js read, POST only on delta) and guarantees
  // attributes are in place even if the customer goes straight from the builder
  // to checkout without visiting the cart page.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  // Re-sync on any in-page cart mutation, whatever the theme calls it.
  ['cart:updated', 'cart:refresh', 'cart:change', 'cart:build', 'cart:rerender',
   'ajaxCart.afterCartLoad', 'cart.requestComplete', 'shopify:section:load']
    .forEach(function (evt) { document.addEventListener(evt, run); });

  // Dawn / Horizon-derived themes use the pub-sub bus rather than DOM events.
  if (window.subscribe && window.PUB_SUB_EVENTS && window.PUB_SUB_EVENTS.cartUpdate) {
    window.subscribe(window.PUB_SUB_EVENTS.cartUpdate, run);
  }

  /**
   * Catch-all: observe Shopify's own cart mutation endpoints. Theme-agnostic,
   * so it works regardless of which events (if any) the theme dispatches.
   */
  var nativeFetch = window.fetch;
  window.fetch = function (input) {
    var url = (typeof input === 'string' ? input : (input && input.url)) || '';
    var isCartMutation = /\/cart\/(add|change|update|clear)(\.js)?/.test(url) &&
                         !/attributes/.test(url);
    var result = nativeFetch.apply(this, arguments);
    if (isCartMutation && !/\/cart\/update\.js/.test(url)) {
      result.then(function () { setTimeout(run, 150); }).catch(function () {});
    }
    return result;
  };

  // Older themes mutate the cart over XHR rather than fetch.
  var openXhr = XMLHttpRequest.prototype.open;
  XMLHttpRequest.prototype.open = function (method, url) {
    this.addEventListener('load', function () {
      if (/\/cart\/(add|change|clear)(\.js)?/.test(url || '')) setTimeout(run, 150);
    });
    return openXhr.apply(this, arguments);
  };

  // Exposed for manual testing from the console: S3D_SYNC()
  window.S3D_SYNC = run;

  // Final safety net: sync immediately before checkout submission.
  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    if (e.target.closest('[name="checkout"], [href*="/checkout"], .cart__checkout-button, .cart__checkout')) {
      run();
    }
  }, true);
})();